<template>
  <div :class="$style.root">
    <SCAInputBase v-for="bound in bounds" :key="bound.key" :alert="alert">
      <input
        :ref="(el) => setInput(bound.key, el)"
        :value="bound.model.value"
        :type="numeric ? 'number' : 'text'"
        :min="numeric ? 0 : undefined"
        :step="numeric ? 'any' : undefined"
        :placeholder="bound.placeholder"
        :name="bound.name"
        autocomplete="off"
        data-lpignore="true"
        data-form-type="other"
        :test-id="bound.testId"
        @input="onInput(bound.model, $event)"
        @keydown.enter.prevent="emit('submit')"
      />
      <template v-if="removable && bound.last" #after>
        <ConditionRemoveButton @remove="emit('remove')" />
      </template>
    </SCAInputBase>
  </div>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance, Ref } from "vue";
import { computed } from "vue";

import { $t } from "../../../i18n";
import SCAInputBase from "../../SCAInputBase.vue";
import ConditionRemoveButton from "./ConditionRemoveButton.vue";

const props = withDefaults(
  defineProps<{
    numeric?: boolean;
    /** Between: a second input for the To bound, stacked below the first. */
    range?: boolean;
    removable?: boolean;
    alert?: boolean;
  }>(),
  { numeric: false, range: false, removable: false, alert: false },
);

const emit = defineEmits<{
  remove: [];
  submit: [];
}>();

const start = defineModel<string>("start", { required: true });
const end = defineModel<string>("end", { default: "" });

const bounds = computed(() => {
  const first = {
    key: "start",
    model: start,
    placeholder: props.range ? $t("columnFilter.range.from") : $t("common.action.enterValue"),
    name: "column-filter-term",
    testId: "column-filter-input",
    last: !props.range,
  };
  if (!props.range) return [first];
  return [
    first,
    {
      key: "end",
      model: end,
      placeholder: $t("columnFilter.range.to"),
      name: "column-filter-term-to",
      testId: "column-filter-input-to",
      last: true,
    },
  ];
});

function onInput(model: Ref<string>, event: Event) {
  model.value = (event.target as HTMLInputElement).value;
}

const inputs = new Map<string, HTMLInputElement>();

function setInput(key: string, el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLInputElement) inputs.set(key, el);
  else inputs.delete(key);
}

defineExpose({ focus: () => inputs.get("start")?.focus() });
</script>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
